import { fetchBot } from '@/services/services';
import { EMPLOYER_EXTENSION } from '@bariendo/lib/extensions';
import { getExtension, MedplumClient } from '@medplum/core';
import type { SetPatientIntakeSelectionInput } from '../../../../../../bots/src/handlers/set-patient-intake-selection';

/**
 * Writes the employer the PN consultation recorded to `EMPLOYER_EXTENSION` on
 * the Patient (BAR-2918), the field Customer.io's `employer` trait and the
 * patient lists read.
 *
 * Through `set-patient-intake-selection`, not a direct write: Patient Access
 * works this card, and `CareIntakeAccessPolicy` has Patient readonly. The bot
 * writes only its allow-listed extensions. An unchanged employer writes
 * nothing, so a Save to chart on the Employer track, where the box shows the
 * employer already on file, leaves the Patient alone. Returns whether it wrote.
 */
export async function writePatientEmployer(
  medplum: MedplumClient,
  patientId: string,
  employer: string
): Promise<boolean> {
  // `no-cache`: MedplumClient answers reads from its own cache for about a
  // minute, and the comparison must be against the stored Patient.
  const stored = await medplum.readResource('Patient', patientId, { cache: 'no-cache' });
  if (getExtension(stored, EMPLOYER_EXTENSION)?.valueString === employer) return false;

  const input: SetPatientIntakeSelectionInput = { patientId, url: EMPLOYER_EXTENSION, value: employer };
  await fetchBot(medplum, 'set-patient-intake-selection', input);
  return true;
}

/**
 * Fills an empty Intake and billing selection (procedure or Location) from the
 * PN card v2.0's save (BAR-3000). Never overwrites one set by hand: the bot
 * checks the stored Patient and writes only when it holds no value for `url`.
 */
export async function fillIntakeSelectionIfEmpty(
  medplum: MedplumClient,
  patientId: string,
  url: string,
  value: string
): Promise<void> {
  const input: SetPatientIntakeSelectionInput = { patientId, url, value, onlyIfEmpty: true };
  await fetchBot(medplum, 'set-patient-intake-selection', input);
}
